import { useContext, useEffect } from 'react';

import { SidePanelContext } from './context';

interface UseSidePanelArgs {
  id: string;
  open: boolean;
  defaultWidth: number;
  resizable: boolean;
}

/**
 * Registers a page's side panel with the PageLayout-owned SidePanelProvider.
 * Only the open/width/id primitives travel up here; the panel content is
 * portaled separately by the <SidePanel> component.
 */
const useSidePanel = ({ id, open, defaultWidth, resizable }: UseSidePanelArgs) => {
  const context = useContext(SidePanelContext);

  if (!context) {
    throw new Error('useSidePanel must be used within a SidePanelProvider');
  }

  const { register, unregister } = context;

  useEffect(() => {
    if (open) {
      register({ id, defaultWidth, resizable });
    } else {
      unregister(id);
    }

    return () => unregister(id);
  }, [id, open, defaultWidth, resizable, register, unregister]);
};

export default useSidePanel;
